// PST-1: the persistence driver contract. The IDB implementation lives in idb.ts;
// MemoryDriver backs EC-ARC-3 (IndexedDB unavailable) and the unit tests.
// ARC-3a: io imports core/doc only — the camera shape is declared structurally here.
import type { Command } from "../doc/commands";

export interface ViewState {
  camera: { cx: number; cy: number; scale: number }; // structurally = engine Camera
  activeLayerId: string;
  /** ANN-7 construction guides — infinite lines, not entities, never exported. */
  guides?: { origin: { x: number; y: number }; dir: { x: number; y: number } }[];
}

export interface ProjectMeta {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  sizeBytes: number;
  thumbnail?: Blob;
  viewState?: ViewState;
}

export interface HistoryTail {
  undo: Command[];
  redo: Command[];
}

export interface PersistDriver {
  readonly persistent: boolean; // false for MemoryDriver → EC-ARC-3 banner
  getDoc(projectId: string): Promise<unknown | undefined>;
  putDoc(projectId: string, json: unknown): Promise<void>;
  deleteDoc(projectId: string): Promise<void>;
  listMeta(): Promise<ProjectMeta[]>;
  getMeta(projectId: string): Promise<ProjectMeta | undefined>;
  putMeta(meta: ProjectMeta): Promise<void>;
  deleteMeta(projectId: string): Promise<void>;
  getHistory(projectId: string): Promise<HistoryTail | undefined>;
  putHistory(projectId: string, tail: HistoryTail): Promise<void>;
  deleteHistory(projectId: string): Promise<void>;
  getSetting<T>(key: string): Promise<T | undefined>;
  putSetting(key: string, value: unknown): Promise<void>;
  putAsset(hash: string, blob: Blob): Promise<void>;
  getAsset(hash: string): Promise<Blob | undefined>;
  deleteAsset(hash: string): Promise<void>;
  listAssetHashes(): Promise<string[]>;
}

export class MemoryDriver implements PersistDriver {
  readonly persistent = false;
  private docs = new Map<string, unknown>();
  private meta = new Map<string, ProjectMeta>();
  private history = new Map<string, HistoryTail>();
  private settings = new Map<string, unknown>();
  private assets = new Map<string, Blob>();

  async getDoc(id: string) {
    return this.docs.get(id) === undefined ? undefined : structuredClone(this.docs.get(id));
  }
  async putDoc(id: string, json: unknown) {
    this.docs.set(id, structuredClone(json));
  }
  async deleteDoc(id: string) {
    this.docs.delete(id);
  }
  async listMeta() {
    return [...this.meta.values()].map((m) => ({ ...m }));
  }
  async getMeta(id: string) {
    const m = this.meta.get(id);
    return m ? { ...m } : undefined;
  }
  async putMeta(meta: ProjectMeta) {
    this.meta.set(meta.id, { ...meta });
  }
  async deleteMeta(id: string) {
    this.meta.delete(id);
  }
  async getHistory(id: string) {
    const t = this.history.get(id);
    return t ? structuredClone(t) : undefined;
  }
  async putHistory(id: string, tail: HistoryTail) {
    this.history.set(id, structuredClone(tail));
  }
  async deleteHistory(id: string) {
    this.history.delete(id);
  }
  async getSetting<T>(key: string) {
    return this.settings.get(key) as T | undefined;
  }
  async putSetting(key: string, value: unknown) {
    this.settings.set(key, structuredClone(value));
  }
  async putAsset(hash: string, blob: Blob) {
    this.assets.set(hash, blob);
  }
  async getAsset(hash: string) {
    return this.assets.get(hash);
  }
  async deleteAsset(hash: string) {
    this.assets.delete(hash);
  }
  async listAssetHashes() {
    return [...this.assets.keys()];
  }
}
